import React, { useState, useEffect } from 'react';
import { RevoltModal } from '../../../../../components/RevoltModal';
import { RevoltInput } from '../../../../../components/RevoltInput';
import { RevoltButton } from '../../../../../components/RevoltButton';
import { RevoltSwitch } from '../../../../../components/RevoltSwitch';
import DropdownSelect from '../../../../../components/DropdownSelect';
import './ProductDownloadsModal.css';

export function ProductDownloadsModal({
  isOpen,
  onClose,
  currentDownloads = [],
  onUpdateDownloads
}) {
  const normalizeDownloadForUI = (d) => {
    if (!d || typeof d !== 'object') {
      return { url: '', name: '', fileSize: '' };
    }

    if (d.mode === 'revolt-desktop') {
      return {
        mode: 'revolt-desktop',
        channel: typeof d.channel === 'string' && d.channel.trim() ? d.channel.trim().toLowerCase() : 'main',
        name: typeof d.name === 'string' ? d.name : '',
        showVersionBadge: typeof d.showVersionBadge === 'boolean' ? d.showVersionBadge : true,
      };
    }

    return {
      url: typeof d.url === 'string' ? d.url : '',
      name: typeof d.name === 'string' ? d.name : '',
      fileSize: typeof d.fileSize === 'string' ? d.fileSize : '',
      fileName: typeof d.fileName === 'string' ? d.fileName : '',
    };
  };

  const [downloads, setDownloads] = useState(currentDownloads.map(normalizeDownloadForUI));

  // Add form state
  const [downloadMode, setDownloadMode] = useState('legacy'); // legacy | revolt-desktop
  const [newUrl, setNewUrl] = useState('');
  const [newName, setNewName] = useState('');
  const [newFileSize, setNewFileSize] = useState('');
  const [newChannel, setNewChannel] = useState('main');
  const [newShowVersionBadge, setNewShowVersionBadge] = useState(true);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValues, setEditValues] = useState({
    mode: 'legacy', // legacy | revolt-desktop
    name: '',
    url: '',
    fileSize: '',
    channel: 'main',
    showVersionBadge: true,
  });

  // Reset modal state when opened
  useEffect(() => {
    if (isOpen) {
      setDownloads(currentDownloads.map(normalizeDownloadForUI));

      setDownloadMode('legacy');
      setNewUrl('');
      setNewName('');
      setNewFileSize('');
      setNewChannel('main');
      setNewShowVersionBadge(true);
      setIsSubmitting(false);
      setEditingIndex(null);
    }
  }, [isOpen, currentDownloads]);

  const isElectronDownload = (d) => Boolean(d && d.mode === 'revolt-desktop');

  const handleStartEdit = (index) => {
    const d = downloads[index];
    if (isElectronDownload(d)) {
      setEditValues({
        mode: 'revolt-desktop',
        name: d.name || '',
        url: '',
        fileSize: '',
        channel: d.channel || 'main',
        showVersionBadge: Boolean(d.showVersionBadge),
      });
    } else {
      setEditValues({
        mode: 'legacy',
        name: d.name || '',
        url: d.url || '',
        fileSize: d.fileSize || '',
        channel: 'main',
        showVersionBadge: true,
      });
    }
    setEditingIndex(index);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  const handleSaveEdit = () => {
    if (editingIndex === null) return;

    const target = downloads[editingIndex];
    const next = [...downloads];

    // Revolt Desktop auto-updater mode
    if (isElectronDownload(target) || editValues.mode === 'revolt-desktop') {
      const name = editValues.name.trim();
      const channel = (editValues.channel || '').trim().toLowerCase();
      if (!name || !channel) return;

      next[editingIndex] = {
        mode: 'revolt-desktop',
        channel,
        name,
        showVersionBadge: Boolean(editValues.showVersionBadge),
      };
    } else {
      // Legacy static link
      const name = editValues.name.trim();
      const url = editValues.url.trim();
      if (!name || !url) return;

      next[editingIndex] = {
        ...target,
        url,
        name,
        fileSize: (editValues.fileSize || '').trim(),
      };
    }

    setDownloads(next);
    setEditingIndex(null);
  };

  const handleAddDownload = () => {
    const name = newName.trim();

    if (downloadMode === 'revolt-desktop') {
      const channel = (newChannel || '').trim().toLowerCase();
      if (!name || !channel) return;

      setDownloads([
        ...downloads,
        {
          mode: 'revolt-desktop',
          channel,
          name,
          showVersionBadge: Boolean(newShowVersionBadge),
        },
      ]);

      setNewName('');
      setNewChannel('main');
      setNewShowVersionBadge(true);
      return;
    }

    // Legacy static link
    const url = newUrl.trim();
    if (!url || !name) return;

    setDownloads([
      ...downloads,
      {
        url,
        name,
        fileSize: newFileSize.trim(),
      },
    ]);
    setNewUrl('');
    setNewName('');
    setNewFileSize('');
  };

  const handleRemoveDownload = (indexToRemove) => {
    setDownloads(downloads.filter((_, i) => i !== indexToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    try {
      await onUpdateDownloads(downloads);
      onClose();
    } catch (error) {
      console.error('Failed to update downloads:', error);
      alert('Failed to update downloads. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <RevoltModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Product Downloads"
      containerClassName="modal-800"
    >
      <div className="product-downloads-modal">
        <div className="product-downloads-columns">
          {/* Left Column - Current Downloads */}
          <div className="product-downloads-left">
            <div className="modal-section">
              <label className="modal-label">Downloadable Content</label>
              {downloads.length > 0 ? (
                <div className="downloads-list">
                  {downloads.map((d, index) => (
                    <div key={index} className="download-item">
                      {editingIndex === index ? (
                        <>
                          <div className="download-item-edit-fields">
                            <RevoltInput
                              type="text"
                              value={editValues.name}
                              onChange={(e) =>
                                setEditValues((v) => ({ ...v, name: e.target.value }))
                              }
                              placeholder="Display name"
                              className="download-edit-input"
                            />

                            {isElectronDownload(downloads[index]) ? (
                              <>
                                <RevoltInput
                                  type="text"
                                  value={editValues.channel}
                                  onChange={(e) =>
                                    setEditValues((v) => ({
                                      ...v,
                                      channel: e.target.value,
                                    }))
                                  }
                                  placeholder="main / beta / dev"
                                  className="download-edit-input download-edit-size"
                                />
                                <RevoltSwitch
                                  id="product-downloads-modal-edit-show-version-badge"
                                  checked={Boolean(editValues.showVersionBadge)}
                                  onChange={(checked) =>
                                    setEditValues((v) => ({
                                      ...v,
                                      showVersionBadge: checked,
                                    }))
                                  }
                                  label="Show version badge"
                                />
                              </>
                            ) : (
                              <>
                                <RevoltInput
                                  type="url"
                                  value={editValues.url}
                                  onChange={(e) =>
                                    setEditValues((v) => ({ ...v, url: e.target.value }))
                                  }
                                  placeholder="https://..."
                                  className="download-edit-input"
                                />
                                <RevoltInput
                                  type="text"
                                  value={editValues.fileSize}
                                  onChange={(e) =>
                                    setEditValues((v) => ({
                                      ...v,
                                      fileSize: e.target.value,
                                    }))
                                  }
                                  placeholder="e.g. 25 MB"
                                  className="download-edit-input download-edit-size"
                                />
                              </>
                            )}
                          </div>
                          <div className="download-item-actions">
                            <button
                              type="button"
                              className="download-btn download-save"
                              onClick={handleSaveEdit}
                              disabled={
                                isElectronDownload(downloads[index])
                                  ? !editValues.name.trim() ||
                                    !String(editValues.channel || '').trim()
                                  : !editValues.name.trim() || !editValues.url.trim()
                              }
                              title="Save"
                            >
                              <i className="fa-solid fa-check"></i>
                            </button>
                            <button
                              type="button"
                              className="download-btn download-cancel"
                              onClick={handleCancelEdit}
                              title="Cancel"
                            >
                              <i className="fa-solid fa-times"></i>
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="download-item-info">
                            <span className="download-item-name">{d.name}</span>
                            {d.mode === 'revolt-desktop' ? (
                              <span className="download-item-size">{d.channel}</span>
                            ) : (
                              d.fileSize && (
                                <span className="download-item-size">{d.fileSize}</span>
                              )
                            )}
                          </div>
                          <div className="download-item-actions">
                            <button
                              type="button"
                              className="download-btn download-edit"
                              onClick={() => handleStartEdit(index)}
                              aria-label={`Edit ${d.name}`}
                              title="Edit"
                            >
                              <i className="fa-solid fa-pen"></i>
                            </button>
                            <button
                              type="button"
                              className="download-btn download-remove"
                              onClick={() => handleRemoveDownload(index)}
                              aria-label={`Remove ${d.name}`}
                              title="Remove"
                            >
                              <i className="fa-solid fa-times"></i>
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-downloads">No downloads yet</p>
              )}
            </div>
          </div>

          {/* Right Column - Add New Download */}
          <div className="product-downloads-right">
            <div className="modal-section">
              <label className="modal-label">Add Download</label>

              <div className="add-download-fields" style={{ marginBottom: 12 }}>
                <label style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
                  Type
                </label>
                <div style={{ marginTop: 6 }}>
                  <DropdownSelect
                    id="product-downloads-modal-type"
                    value={downloadMode}
                    options={[
                      { value: 'legacy', label: 'Static download link' },
                      { value: 'revolt-desktop', label: 'Revolt Desktop (updater channel)' },
                    ]}
                    onValueChange={(v) => setDownloadMode(v)}
                  />
                </div>
              </div>

              <div className="add-download-fields">
                <RevoltInput
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Display name (e.g. Windows Installer)"
                  className="add-download-name"
                />

                {downloadMode === 'revolt-desktop' ? (
                  <>
                    <RevoltInput
                      type="text"
                      value={newChannel}
                      onChange={(e) => setNewChannel(e.target.value)}
                      placeholder="main / beta / dev"
                      className="add-download-size"
                    />
                    <RevoltSwitch
                      id="product-downloads-modal-add-show-version-badge"
                      checked={Boolean(newShowVersionBadge)}
                      onChange={setNewShowVersionBadge}
                      label="Show version badge"
                    />
                  </>
                ) : (
                  <>
                    <RevoltInput
                      type="url"
                      value={newUrl}
                      onChange={(e) => setNewUrl(e.target.value)}
                      placeholder="https://example.com/file.zip"
                      className="add-download-url"
                    />
                    <RevoltInput
                      type="text"
                      value={newFileSize}
                      onChange={(e) => setNewFileSize(e.target.value)}
                      placeholder="File size (e.g. 25 MB)"
                      className="add-download-size"
                    />
                  </>
                )}
              </div>
              <RevoltButton
                type="button"
                variant="outline"
                size="small"
                onClick={handleAddDownload}
                disabled={
                  downloadMode === 'revolt-desktop'
                    ? !newChannel.trim() || !newName.trim()
                    : !newUrl.trim() || !newName.trim()
                }
                className="add-download-btn"
              >
                <i className="fa-solid fa-plus"></i>
                Add
              </RevoltButton>
              <div className="form-help">Add downloadable files for purchasers.</div>
            </div>
          </div>
        </div>

        {/* Modal Actions */}
        <div className="modal-actions">
          <RevoltButton
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </RevoltButton>
          <RevoltButton
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i>
                Updating...
              </>
            ) : (
              'Update Downloads'
            )}
          </RevoltButton>
        </div>
      </div>
    </RevoltModal>
  );
}
